/**
 * Acquisition attribution: where a visitor came from, plus the anon ID that
 * ties visit to CTA click to login in the GASv3 marketing dashboards.
 *
 * Mirrors WAC's `getAndSetUtms` so both properties produce the same values:
 *  - `anonId` is read, never minted, from the shared `ajs_anonymous_id` cookie.
 *    Null when absent, and the GASv3 client mints one on its first event.
 *  - The UTM bag persists to `localStorage["marketingContext"]` in WAC's
 *    envelope: `{ domain, utm: { source, medium, campaign, content,
 *    sfdcCampaignId } }`. Each property persists locally for now; GUPS backfill
 *    arrives with the Analytics Wrapper.
 *
 * Replacement semantics, not first-touch: UTMs in the URL overwrite the stored
 * bag, otherwise the stored bag is restored, otherwise UTMs are null.
 *
 * The stored bag expires 7 days after its last capture. UTMs are deliberately
 * left in the URL, so a reload, or any event fired while they are still there,
 * re-captures and restarts the week. Reads alone never extend it.
 *
 * Each event combines three signals in descending fidelity: the UTM bag, the
 * paid-click IDs (`gclid`, `fbclid`, `msclkid`, `li_fat_id`), then
 * `referrerHost` (blank for native apps, useful for search and inbound links).
 *
 * Best-effort throughout: storage failures (Brave, Safari ITP, private mode)
 * degrade to in-memory and never throw.
 */
import { captureLandingHash, extractHashQuery } from '../deep-link';

const STORAGE_KEY_MARKETING_CONTEXT = 'marketingContext';
const COOKIE_NAME_ANON_ID = 'ajs_anonymous_id';

/**
 * How long a captured UTM bag keeps attributing events. Measured from the last
 * write, so anything that re-captures the same UTMs restarts the clock.
 */
const MARKETING_CONTEXT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type TrafficSource = 'paid' | 'organic' | 'internal' | 'referral' | 'direct';

/**
 * The attribute bag [`AnalyticsProvider`](../AnalyticsProvider.tsx) merges into
 * every attributed event. Field names mirror the `AttributionContext` anchor in
 * analytics.spec.yaml and the Marketing Analytics Pipeline runbook, so events
 * stay forward-compatible with the Analytics Wrapper.
 */
export type AttributionPayload = {
	anonId: string | null;

	trafficSource: TrafficSource;
	trafficSourceDetail: string | null;

	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
	utmContent: string | null;
	utmTerm: string | null;
	utmSfdcCampaignId: string | null;

	gclid: string | null;
	fbclid: string | null;
	msclkid: string | null;
	liFatId: string | null;

	referrerHost: string | null;
	/**
	 * Misnamed: `window.location.pathname` at call time, NOT the session's entry
	 * path, because this runs per event and routing is pushState. Do not slice on
	 * it; give the event its own `page` (see `hooks/useMicrositePage.ts`).
	 */
	landingPath: string;
};

/**
 * The persisted UTM sub-payload. Mirrors WAC's `marketingContext.utm` shape so
 * the key stays interoperable if this property adopts the shared wrapper.
 */
type StoredMarketingContext = {
	domain: string;
	/**
	 * Epoch ms of the last write, for the TTL check. Additive to WAC's envelope, so
	 * a WAC reader ignores it, but note this module's reader is the stricter of the
	 * two: a bag without `storedAt` is deleted, not just ignored. Safe while each
	 * property persists locally, and something to revisit when GUPS backfill starts
	 * writing WAC-shaped bags into this origin.
	 */
	storedAt: number;
	utm: {
		source: string;
		medium: string;
		campaign: string;
		content: string;
		sfdcCampaignId: string;
	};
};

const SEARCH_ENGINE_HOSTS: ReadonlyArray<{ match: string; family: string }> = [
	{ match: 'google.', family: 'google' },
	{ match: 'bing.com', family: 'bing' },
	{ match: 'duckduckgo.com', family: 'duckduckgo' },
	{ match: 'search.yahoo.', family: 'yahoo' },
	{ match: 'baidu.com', family: 'baidu' },
	{ match: 'yandex.', family: 'yandex' },
	{ match: 'ecosia.org', family: 'ecosia' },
	{ match: 'search.brave.com', family: 'brave' },
];

const INTERNAL_HOST_SUFFIXES: readonly string[] = [
	'atlassian.com',
	'atlassian.net',
	'jira.com',
	'confluence.com',
	'loom.com',
	'bitbucket.org',
	'trello.com',
	'statuspage.io',
];

const detailFromClickId = (
	gclid: string | null,
	fbclid: string | null,
	msclkid: string | null,
	liFatId: string | null,
): string | null => {
	if (gclid) return 'google-ads';
	if (fbclid) return 'meta-ads';
	if (msclkid) return 'bing-ads';
	if (liFatId) return 'linkedin-ads';
	return null;
};

const safeHostname = (rawUrl: string): string | null => {
	if (!rawUrl) return null;
	try {
		return new URL(rawUrl).hostname.toLowerCase();
	} catch {
		return null;
	}
};

const matchesSearchEngine = (host: string): string | null => {
	for (const engine of SEARCH_ENGINE_HOSTS) {
		if (host.includes(engine.match)) return engine.family;
	}
	return null;
};

const matchesInternalHost = (host: string): boolean =>
	INTERNAL_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));

const safeReadLocalStorage = (key: string): string | null => {
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
};

const safeRemoveLocalStorage = (key: string): void => {
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		window.localStorage.removeItem(key);
	} catch {
		// noop — same blocked-storage tolerance as the read/write helpers.
	}
};

const safeWriteLocalStorage = (key: string, value: string): void => {
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		window.localStorage.setItem(key, value);
	} catch {
		// Blocked storage APIs (Brave / Safari ITP / private mode) must
		// not crash the page. We silently fall back to in-memory only —
		// the event still fires, just without persisted UTMs across
		// SPA navigations.
	}
};

/**
 * Reads the Segment-style `ajs_anonymous_id` cookie. Matches WAC's
 * convention: `decodeURIComponent` then strip surrounding single/double
 * quotes (Segment writes the value as a JSON string). Returns null when
 * the cookie is missing — we deliberately do NOT mint our own anon ID,
 * because doing so would fragment identity stitching across Atlassian
 * properties.
 */
export const readAnonId = (): string | null => {
	if (typeof document === 'undefined') return null;
	try {
		const all = document.cookie ? document.cookie.split('; ') : [];
		for (const entry of all) {
			const eqIdx = entry.indexOf('=');
			if (eqIdx === -1) continue;
			if (entry.slice(0, eqIdx) === COOKIE_NAME_ANON_ID) {
				const raw = entry.slice(eqIdx + 1);
				return decodeURIComponent(raw).replace(/['"]+/g, '') || null;
			}
		}
	} catch {
		// fall through
	}
	return null;
};

const readStoredContext = (): StoredMarketingContext | null => {
	const raw = safeReadLocalStorage(STORAGE_KEY_MARKETING_CONTEXT);
	if (!raw) return null;

	// Evict rather than ignore, so an unusable bag is not re-parsed and
	// re-rejected on every later event.
	const discard = (): null => {
		safeRemoveLocalStorage(STORAGE_KEY_MARKETING_CONTEXT);
		return null;
	};

	let parsed: Partial<StoredMarketingContext>;
	try {
		parsed = JSON.parse(raw) as Partial<StoredMarketingContext>;
	} catch {
		return discard();
	}

	if (!parsed || !parsed.utm) return discard();

	// No usable `storedAt` means unknown age (written before the TTL shipped, or
	// by something else); a future one means the clock moved. Neither can be aged,
	// so drop it rather than attribute events to it forever.
	const age =
		typeof parsed.storedAt === 'number' && Number.isFinite(parsed.storedAt)
			? Date.now() - parsed.storedAt
			: NaN;
	if (!(age >= 0 && age < MARKETING_CONTEXT_TTL_MS)) return discard();

	return parsed as StoredMarketingContext;
};

/** Stamps `storedAt`, so every write restarts the TTL. */
const writeStoredContext = (context: Omit<StoredMarketingContext, 'storedAt'>): void => {
	safeWriteLocalStorage(
		STORAGE_KEY_MARKETING_CONTEXT,
		JSON.stringify({ ...context, storedAt: Date.now() }),
	);
};

/**
 * Pure resolver — builds an `AttributionPayload` from raw inputs. No
 * side effects, fully unit-testable.
 *
 * Resolution precedence:
 *   1. UTMs present                        → `paid` if utm_medium=cpc, else `referral`
 *   2. paid click ID present               → `paid`
 *   3. referrer hostname = search engine   → `organic`
 *   4. referrer hostname = internal Atlassian → `internal`
 *   5. referrer hostname = anything else   → `referral`
 *   6. otherwise                           → `direct`
 */
export const resolveAttribution = (
	search: string,
	referrer: string,
	pathname: string,
	anonId: string | null,
): AttributionPayload => {
	const params = new URLSearchParams(search);

	const utmSource = params.get('utm_source');
	const utmMedium = params.get('utm_medium');
	const utmCampaign = params.get('utm_campaign');
	const utmContent = params.get('utm_content');
	const utmTerm = params.get('utm_term');
	const utmSfdcCampaignId = params.get('utm_sfdc-campaign_id');

	const gclid = params.get('gclid');
	const fbclid = params.get('fbclid');
	const msclkid = params.get('msclkid');
	const liFatId = params.get('li_fat_id');

	const referrerHost = safeHostname(referrer);

	let trafficSource: TrafficSource = 'direct';
	let trafficSourceDetail: string | null = null;

	if (utmSource) {
		trafficSource = utmMedium === 'cpc' ? 'paid' : 'referral';
		trafficSourceDetail = utmSource;
	} else if (gclid || fbclid || msclkid || liFatId) {
		trafficSource = 'paid';
		trafficSourceDetail = detailFromClickId(gclid, fbclid, msclkid, liFatId);
	} else if (referrerHost) {
		const engine = matchesSearchEngine(referrerHost);
		if (engine) {
			trafficSource = 'organic';
			trafficSourceDetail = engine;
		} else if (matchesInternalHost(referrerHost)) {
			trafficSource = 'internal';
			trafficSourceDetail = referrerHost;
		} else {
			trafficSource = 'referral';
			trafficSourceDetail = referrerHost;
		}
	}

	return {
		anonId,
		trafficSource,
		trafficSourceDetail,
		utmSource,
		utmMedium,
		utmCampaign,
		utmContent,
		utmTerm,
		utmSfdcCampaignId,
		gclid,
		fbclid,
		msclkid,
		liFatId,
		referrerHost,
		landingPath: pathname,
	};
};

const stripLeadingQuestion = (search: string): string =>
	search.startsWith('?') ? search.slice(1) : search;

/**
 * Combines two query strings; params in `primary` win over `secondary`
 * (`URLSearchParams.get` returns the first occurrence).
 */
const mergeSearch = (primary: string, secondary: string): string => {
	const parts = [stripLeadingQuestion(primary), stripLeadingQuestion(secondary)].filter(Boolean);
	return parts.length > 0 ? `?${parts.join('&')}` : '';
};

/**
 * The attribution payload as of this call. Called once per event, so it is also
 * where a UTM-carrying URL gets captured and the TTL restamped (see the file
 * header for the replacement and expiry rules).
 *
 * UTMs in the URL are persisted and returned. Otherwise an unexpired stored bag
 * is returned alongside the live referrer and click-id signals. Failing both,
 * whatever the resolver derived, down to `direct` with null UTMs.
 *
 * SSR-safe: returns an in-memory `direct` payload without `window`.
 */
export const getAttribution = (): AttributionPayload => {
	if (typeof window === 'undefined') {
		return resolveAttribution('', '', '/', null);
	}

	const anonId = readAnonId();
	// Allows for UTMs after the hash (`#CLI?utm_source=...`),
	// so `window.location.search` is empty. The hash fragment's query-part is
	// parsed as a fallback; a real `?query` still wins.
	const hashQuery = extractHashQuery(captureLandingHash());
	const search = mergeSearch(window.location.search, hashQuery);
	const pathname = window.location.pathname;
	const referrer = typeof document !== 'undefined' ? document.referrer : '';
	const live = resolveAttribution(search, referrer, pathname, anonId);

	if (live.utmSource || live.utmMedium || live.utmCampaign || live.utmContent || live.utmTerm) {
		// URL has UTMs — overwrite stored context (replacement semantics).
		writeStoredContext({
			domain: window.location.hostname,
			utm: {
				source: live.utmSource ?? '',
				medium: live.utmMedium ?? '',
				campaign: live.utmCampaign ?? '',
				content: live.utmContent ?? '',
				sfdcCampaignId: live.utmSfdcCampaignId ?? '',
			},
		});
		return live;
	}

	// No UTMs in URL — fall back to persisted UTMs (if any) and re-derive
	// trafficSource using the persisted UTMs + live referrer.
	const stored = readStoredContext();
	if (!stored) {
		return live;
	}

	const restoredUtmSource = stored.utm.source || null;
	const restoredUtmMedium = stored.utm.medium || null;
	let trafficSource: TrafficSource = live.trafficSource;
	let trafficSourceDetail: string | null = live.trafficSourceDetail;
	if (restoredUtmSource) {
		trafficSource = restoredUtmMedium === 'cpc' ? 'paid' : 'referral';
		trafficSourceDetail = restoredUtmSource;
	}

	return {
		...live,
		trafficSource,
		trafficSourceDetail,
		utmSource: restoredUtmSource,
		utmMedium: restoredUtmMedium,
		utmCampaign: stored.utm.campaign || null,
		utmContent: stored.utm.content || null,
		utmTerm: live.utmTerm,
		utmSfdcCampaignId: stored.utm.sfdcCampaignId || null,
	};
};

/** Test-only — clears persisted marketing context. Do not call from app code. */
export const __resetAttributionForTests = (): void => {
	safeRemoveLocalStorage(STORAGE_KEY_MARKETING_CONTEXT);
};
